import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { CompanySidebar } from "../components/CompanySidebar";
import { CompanyCollaborationModal } from "../components/CompanyCollaborationModal";
import { CompanyAdvisorModal } from "../components/CompanyAdvisorModal";
import { useDealContext } from "../../deal/context";
import { useAdvisors } from "../../advisor/context/AdvisorsContext";
import Loading from "../../../components/Loading";
import { Container, Dialog } from "@mui/material";
import { UserProfileUpdate } from "../../users/components/UserProfileUpdate";
import { useUserContext } from "../../users/context";
import { useFirmContext } from "../../firm/context";
import ClientProvider from "../../../modules/ClientProvider";
import { IMeeting } from "../../../types";
import { useAuth0 } from "@auth0/auth0-react";
import Intercom from "@intercom/messenger-js-sdk/dist";
import { TransitionOverlay } from "../../../components/TransitionOverlay";
import { FadeContainer } from "../../../components/FadeContainer";

export const Company = () => {
    const [openAdvisorId, setOpenAdvisorId] = useState<string | null>(null);
    const { touchpointUuid } = useParams();
    const location = useLocation();
    const { dealContext } = useDealContext();
    const navigate = useNavigate();
    const { advisors, fetchAdvisors } = useAdvisors();
    const { userContext, loadUserContext } = useUserContext();
    const { firmContext } = useFirmContext();
    const { getAccessTokenSilently } = useAuth0();
    const projectHttpClient = ClientProvider.provideProjectClient();

    useEffect(() => {
        if (process.env.REACT_APP_INTERCOM_APP_ID) {
            Intercom({
                app_id: process.env.REACT_APP_INTERCOM_APP_ID,
                user_id: userContext.dbUser?.uuid,
                name: userContext.dbUser?.profile?.name,
                email: userContext.dbUser?.email_address,
                created_at: (new Date(userContext.dbUser?.created_at)).valueOf(), // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
            });
        }
    }, [userContext.dbUser]);
    
    // Sets the initial viewpoint from the URL hash
    useEffect(() => {
        const hashValue = location.hash.slice(1);
        setOpenAdvisorId(hashValue || null);
    }, [location.hash]);

    useEffect(() => {
        if (dealContext.project?.status === "draft" && !/\/setup$/.test(location?.pathname)) {
            navigate("setup");
        }
    }, [location.pathname, dealContext.project?.status]);

    useEffect(() => {
        if (dealContext.project?.id) {
            fetchAdvisors(dealContext.project.id);
            fetchMeetings();
        }
    }, [dealContext.project?.id]);

    // TODO: This is a temporary fix to redirect to the unrated meeting page
    const fetchMeetings = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await projectHttpClient.getMeetings(dealContext.project.id, accessToken);
        if (data) {
            const unratedMeetings = data.meetings.filter((meeting: IMeeting) => 
                meeting.advisor.stage === "advisor" && meeting.event_start && new Date(meeting.event_start) < new Date() && meeting.ratings?.length === 0 && meeting.scheduler?.attendees?.some((attendee: any) => attendee.email === userContext.dbUser?.email_address)
            );

            if (unratedMeetings.length > 0) {
                const meetingType = unratedMeetings[0].scheduler?.type;
                navigate(`/c/meetings/${meetingType}/${unratedMeetings[0].uuid}`);
            }
        }
    }

    if (!dealContext.fetched) {
        return <TransitionOverlay open={true} message="Loading..." />;
    }

    if (dealContext.role === "advisor") {
        navigate("/advisor");
        return;
    }

    return (
        <>
            <aside>
                <CompanySidebar />
            </aside>
            <FadeContainer component="main" className="">
                <Outlet context={{ openViewpointId: openAdvisorId }} />
            </FadeContainer>
            <CompanyCollaborationModal />
            {openAdvisorId && <CompanyAdvisorModal uuid={openAdvisorId} touchpointUuid={touchpointUuid || null} />}
            {userContext.dbUser?.profile && !userContext.dbUser?.profile?.user_reviewed &&
                <Dialog open={true} onClose={() => {}}>
                    <h5>Welcome to the {dealContext.project.name} Advisor Network</h5>
                    <p>Please take a moment to review and update your profile.</p>
                    <UserProfileUpdate user={userContext.dbUser} onUpdate={loadUserContext} />
                </Dialog>
            }
        </>
    );
}